export function dateTime(date: Date) {
  const minuteOffest = date.getTimezoneOffset()
  return new Date(+date - minuteOffest * 60 * 1000)
}

const _MS_PER_DAY = 1000 * 60 * 60 * 24

export const getDifferenceInSeconds = (endDate: Date, startDate: Date) => {
  const seconds = (endDate.getTime() - startDate.getTime()) / 1000
  return seconds
}

export const getDifferenceInDays = (endDate: Date, startDate: Date) => {
  const utc1 = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const utc2 = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())

  return Math.floor((utc2 - utc1) / _MS_PER_DAY)
}

// Pre-compiled regex patterns for isDate (avoid re-allocation per call)
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const NAIVE_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/
const TZ_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/

// Accept these shapes:
// - "YYYY-MM-DD"
// - "YYYY-MM-DDTHH:mm" (optionally :ss[.SSS])
// - "YYYY-MM-DDTHH:mm[:ss[.SSS]]Z" or with ±hh:mm offset
export const isDate = (s: string): boolean => {
  if (typeof s !== 'string') return false
  let str = s.trim()
  if (!str) return false

  // Remove anything after decimal in seconds, for simplicity
  const dotIdx = str.indexOf('.')
  str = dotIdx >= 0 ? str.slice(0, dotIdx) : str

  // 1) Date-only: YYYY-MM-DD  (Safari-safe via component ctor)
  const mDate = DATE_ONLY_RE.exec(str)
  if (mDate) {
    const [, y, m, d] = mDate
    const Y = Number(y),
      M = Number(m),
      D = Number(d)
    const dt = new Date(Y, M - 1, D, 0, 0, 0, 0) // local time
    return !Number.isNaN(dt.getTime()) && dt.getFullYear() === Y && dt.getMonth() === M - 1 && dt.getDate() === D
  }

  // 2) Naive datetime (no TZ): YYYY-MM-DDTHH:mm[:ss[.SSS]]
  const mNaive = NAIVE_DATETIME_RE.exec(str)
  if (mNaive) {
    const [, y, m, d, hh, mi, ss = '0', ms = '0'] = mNaive
    const dt = new Date(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mi),
      Number(ss),
      Number(ms.padEnd(3, '0'))
    ) // local time
    return !Number.isNaN(dt.getTime())
  }

  // 3) TZ-qualified ISO: let the engine parse (Safari handles this)
  const mTz = TZ_DATETIME_RE.test(str)
  if (mTz) return !Number.isNaN(Date.parse(str))

  return false
}

// Helper function to safely convert a value to ISO string, handling Invalid Date objects
export const safeToISOString = (value: string): string => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toISOString()
}

// Helper function to safely convert a value with timezone suffix to ISO string
export const safeToISOStringWithZ = (value: string): string => {
  const date = new Date(value + 'Z')
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toISOString()
}

export const formatShortDate = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export const formatShortDateTime = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}
